/**
 * Make a large phone photo small enough to upload and to read, before it leaves the browser.
 *
 * A current phone photo can be 10–30 MB (48 MP and 200 MP modes). The model refuses an image over
 * 5 MB, so a 6 MB photo used to upload fine and then come back "could not be parsed" (Oct 2026). The
 * server now shrinks such a photo before the model sees it, but a 20 MB upload is slow on a phone
 * connection and is decoded at full size on the server. A citation is still easy to read at 2400 px
 * on its long edge, so a big photo is redrawn at that size as a JPEG here first.
 *
 * A HEIC is converted the same way where the browser can open it (Safari). Where it can't, or anything
 * else goes wrong, the original file is uploaded unchanged and the server deals with it.
 */

/** Photos above this are redrawn. Under the model's per-image limit with room to spare. */
export const SHRINK_ABOVE_BYTES = 3.5 * 1024 * 1024;
export const MAX_LONG_EDGE_PX = 2400;
const JPEG_QUALITY = 0.85;

const REDRAWABLE = /^image\/(jpeg|png|webp)$/i;
const HEIC = /^image\/hei[cf]$/i;

/** Whether a file should be redrawn before upload. PDFs and small photos go as they are. */
export function shouldShrink(file: { size: number; type: string; name: string }): boolean {
  if (HEIC.test(file.type) || /\.hei[cf]$/i.test(file.name)) return true;
  return REDRAWABLE.test(file.type) && file.size > SHRINK_ABOVE_BYTES;
}

/** The redrawn size: the long edge at most MAX_LONG_EDGE_PX, never enlarged. */
export function shrunkDimensions(width: number, height: number): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (longEdge <= MAX_LONG_EDGE_PX) return { width, height };
  const scale = MAX_LONG_EDGE_PX / longEdge;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/** `photo.HEIC` → `photo.jpg`. */
export function jpegName(name: string): string {
  const base = name.replace(/\.[^./\\]+$/, '');
  return `${base || 'photo'}.jpg`;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('This browser cannot open the image'));
    };
    image.src = url;
  });
}

/** The file to upload: a redrawn JPEG when that helps, otherwise the file as chosen. */
export async function shrinkImageForUpload(file: File): Promise<File> {
  if (!shouldShrink(file)) return file;
  try {
    const image = await loadImage(file);
    const { width, height } = shrunkDimensions(image.naturalWidth, image.naturalHeight);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.drawImage(image, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    );
    if (!blob) return file;
    // A HEIC is always worth converting; anything else only when it came out smaller.
    const converting = !REDRAWABLE.test(file.type);
    if (!converting && blob.size >= file.size) return file;
    return new File([blob], jpegName(file.name), { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file;
  }
}
